import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Label, Typography, Select, Button, Img } from "@commonsku/styles";

import {
  OptionProps,
  SingleValue,
  components,
  GroupHeadingProps,
} from "react-select";
import { TemplateOption, GroupedOption } from "./types";
import styled from "styled-components";
import { PublicViewTemplate } from "../../../../interfaces/PublicViewTemplate";
import { usePublicViewTemplates } from "../../../admin-new/hooks/usePublicViewTemplates";
import { SelectOption } from "./SelectOption";
import SelectTemplatePopup from "../../../shop/popups/SelectTemplatePopup/SelectTemplatePopup";
import { BASE_ZINDEX } from "../../../../popup-factory";

const TEMPLATE_PICKER_Z_INDEX = BASE_ZINDEX + 300;

const TemplatePreviewContainer = styled.div`
  &&& {
    display: flex;
    align-items: flex-start;
    width: 100%;
    gap: 24px;
    margin-top: 24px;
    align-self: stretch;
  }
`;

const TemplatePreview = styled.div`
  display: flex;
  justify-content: center;
  flex-basis: 60%;
`;

const TemplatePreviewImage = styled(Img)`
  max-width: 100%;
  height: auto;
`;

const OpenTemplatePickerButton = styled(Button)`
  flex-basis: 40%;
  white-space: nowrap;
`;

const GroupHeadingContainer = styled.div`
  &&& {
    padding: 8px;
  }
  &&& p {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 0;
    text-transform: none;
  }
`;

const GroupHeading = (props: GroupHeadingProps<TemplateOption>) => {
  const { selectProps } = props;
  const { inputValue } = selectProps;

  return inputValue ? null : (
    <GroupHeadingContainer>
      <components.GroupHeading {...props}>
        <Typography.Text>{props.data.label}</Typography.Text>
      </components.GroupHeading>
    </GroupHeadingContainer>
  );
};

const SelectOptionComponentContainer = styled.div`
  display: flex;
  padding: 8px 8px 8px 16px;
`;

const SelectOptionComponent: React.FC<{ label: string }> = ({ label }) => (
  <SelectOptionComponentContainer>{label}</SelectOptionComponentContainer>
);

interface PresentationTemplateSelectProps {
  onUpdateField: (field: string, value: string) => void;
}

const PresentationTemplateSelect = React.memo(
  ({ onUpdateField }: PresentationTemplateSelectProps) => {
    const [currentValue, setCurrentValue] =
      React.useState<TemplateOption | null>(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const {
      templates: fetchedTemplates,
      layouts: fetchedLayouts,
      commonskuTemplates: fetchedCommonskuTemplates,
      companyName: fetchedCompanyName,
    } = usePublicViewTemplates();

    const { layoutOptions, templateOptions, commonskuTemplateOptions } =
      useMemo(() => {
        const fetchedLayoutOptions = fetchedLayouts.map(
          (layout: PublicViewTemplate) => ({
            value: layout.public_view_template_id,
            label: layout.public_view_template_title,
          })
        );

        const fetchedTemplateOptions = fetchedTemplates.map(
          (template: PublicViewTemplate) => ({
            value: template.public_view_template_id,
            label: template.public_view_template_title,
          })
        );

        const fetchedCommonskuTemplateOptions = fetchedCommonskuTemplates.map(
          (template: PublicViewTemplate) => ({
            value: template.public_view_template_id,
            label: template.public_view_template_title,
          })
        );

        return {
          layoutOptions: fetchedLayoutOptions,
          templateOptions: fetchedTemplateOptions,
          commonskuTemplateOptions: fetchedCommonskuTemplateOptions,
        };
      }, [fetchedLayouts, fetchedTemplates, fetchedCommonskuTemplates]);

    const groupedOptions: GroupedOption[] = useMemo(() => {
      return [
        {
          label: `${fetchedCompanyName} Templates`,
          options: templateOptions,
        },
        {
          label: "commonsku Templates",
          options: commonskuTemplateOptions,
        },
        {
          label: "Layouts",
          options: layoutOptions,
        },
      ];
    }, [
      fetchedCompanyName,
      templateOptions,
      commonskuTemplateOptions,
      layoutOptions,
    ]);

    const handleSelectChange = useCallback(
      (option: SingleValue<TemplateOption>) => {
        const isLayout = layoutOptions.some((l) => l.value === option.value);

        // Set the public_view_layout_id or public_view_template_id based on the selection
        onUpdateField(
          isLayout ? "public_view_layout_id" : "public_view_template_id",
          option.value
        );

        // Clear the other field based on the selection
        onUpdateField(
          isLayout ? "public_view_template_id" : "public_view_layout_id",
          ""
        );

        setCurrentValue(option);
      },
      [layoutOptions, onUpdateField]
    );

    const handleOpenPopup = useCallback(() => {
      setIsPopupOpen(true);
    }, []);

    const handleClosePopup = useCallback(() => {
      setIsPopupOpen(false);
    }, []);

    const handleSelectFromPopup = useCallback((selectedTemplate: PublicViewTemplate) => {
      const templateOption: TemplateOption = {
        value: selectedTemplate.public_view_template_id,
        label: selectedTemplate.public_view_template_title,
      };
      handleSelectChange(templateOption);
      setIsPopupOpen(false);
    }, [handleSelectChange]);

    // SelectTemplatePopup expects title and image fields
    const mappedTemplatesForPopup = useMemo(() => {
      const mapTemplate = (template: PublicViewTemplate) => ({
        ...template,
        title: template?.public_view_template_title || template?.public_view_template_name || '',
        image: template?.public_view_template_background || '',
      });

      return [
        ...(fetchedLayouts?.map(mapTemplate) || []),
        ...(fetchedTemplates?.map(mapTemplate) || []),
        ...(fetchedCommonskuTemplates?.map(mapTemplate) || []),
      ];
    }, [fetchedLayouts, fetchedTemplates, fetchedCommonskuTemplates]);

    const selectedMappedTemplate = useMemo(
      () => mappedTemplatesForPopup.find(t => currentValue && t.public_view_template_id === currentValue.value),
      [currentValue, mappedTemplatesForPopup]
    );

    const isLayoutSelected = useMemo(
      () => selectedMappedTemplate && layoutOptions.some((l) => l.value === selectedMappedTemplate.public_view_template_id),
      [selectedMappedTemplate, layoutOptions]
    );

    useEffect(() => {
      if (
        currentValue === null &&
        (templateOptions.length || layoutOptions.length)
      ) {
        const value =
          templateOptions[0] ||
          layoutOptions.find((layout) => layout.label === "Gallery");
        handleSelectChange(value);
      }
    }, [currentValue, templateOptions, layoutOptions, handleSelectChange]);

    return (
      <>
        <Label>Templates</Label>
        <Select
          value={currentValue}
          containerStyles={{ width: "100%" }}
          options={groupedOptions}
          onChange={handleSelectChange}
          inPopup
          components={{
            GroupHeading,
            Option: (props: OptionProps<TemplateOption>) => (
              <SelectOption
                {...props}
                children={<SelectOptionComponent label={props.data.label} />}
              />
            ),
          }}
          isMulti={false}
          singleValueStyles={{
            display: "flex",
            alignItems: "center",
          }}
        />
        <TemplatePreviewContainer>
          {currentValue && (
            <TemplatePreview>
              <TemplatePreviewImage
                src={selectedMappedTemplate?.image || ""}
                alt={currentValue ? currentValue.label : "Template Preview"}
              />
            </TemplatePreview>
          )}
          <OpenTemplatePickerButton
            variant="secondary"
            size="small"
            onClick={handleOpenPopup}
          >
            Open Template Picker
          </OpenTemplatePickerButton>
        </TemplatePreviewContainer>
        {isPopupOpen && (
          <SelectTemplatePopup
            onClose={handleClosePopup}
            zIndex={TEMPLATE_PICKER_Z_INDEX}
            layouts={mappedTemplatesForPopup}
            templateData={{}}
            selectedTheme={(currentValue && isLayoutSelected) ? selectedMappedTemplate : null}
            selectedTemplate={(currentValue && !isLayoutSelected) ? selectedMappedTemplate : null}
            initialFilters={[]}
            onClickSelect={handleSelectFromPopup}
            onClickFavouriteTheme={() => {}} // Not yet implemented
            isShop={false}
            entity={{ company_data: { tenant_name: fetchedCompanyName } }}
            labelTitle="Presentation"
          />
        )}
      </>
    );
  }
);

export default PresentationTemplateSelect;
